/**
 * What the signed-in person may OPEN, read once for the whole console (ADR 0196).
 *
 * `GET /v1/profile` carries `permissions`: the catalog keys the caller's organization role resolves
 * to, the same set every account route is gated with (`route-permissions.test.ts`). The console
 * used to read it in three places — the admin console's nav, the Settings rail, the release-gap
 * banner — and in NONE of them for the main navigation, so the Admin, Developer and Connect areas
 * were offered to every role. Measured on the launch cell 2026-09-16 as a `viewer`: the whole Admin
 * Settings console, Security policy and Document retention included, drawn for somebody whose
 * entire permission set is `reports.view`. Every save would have been refused — the server is the
 * authority and stayed it — but a console that offers a person fourteen screens they cannot use is
 * the frontend half of the defect, and the founder photographed it.
 *
 * ## `requires` decides what is OFFERED, never what is allowed
 *
 * This module decides which rows to draw. The API decides what happens when one is used, and
 * nothing here may be read as authorization (CLAUDE.md §2, never #3). Three consequences:
 *
 *   * A gated row is offered when the profile says the caller holds its key, withheld while the
 *     profile is still being asked, and OFFERED when the read failed or answered without a list
 *     (`unknown`) — a transient failure must never hide an administrator's tools, and each screen's
 *     own 403 is the refusal then. ADR 0121 (amended) set that rule for the Settings rail and it is
 *     kept exactly.
 *   * A deep link past the nav still lands on the screen, which says in a sentence who may open it
 *     (`AdministratorsOnly`) or shows the server's 403 in words. Nothing here is a wall.
 *   * The keys are the catalog's, spelled once beside the row they gate, and a row's key is the
 *     same key the API gates that row's route with — so the nav cannot promise a screen the route
 *     refuses without somebody typing two different keys for one thing.
 *
 * ## One read, and a cache for the first paint
 *
 * The provider sits at the console's root and reads the profile once; `AdminShell` used to read it
 * again for its own nav and now takes this one, so the outer nav and the console cannot disagree
 * about the same person on the same paint. A console mounted WITHOUT the provider — a test that
 * draws the admin console alone — still reads for itself (`useProfileOffering`), so nothing that
 * worked stops working.
 *
 * The last answer is cached in `localStorage`, keyed by the account that received it, so a
 * returning administrator's nav is right from the first paint rather than growing an Admin entry
 * 1.6 s later (the measured cost of one authenticated request). Same discipline as the theme and
 * workflow caches (ADR 0063): every read and write in try/catch, the fresh answer always replaces
 * the cached one, and a cache for a DIFFERENT account is ignored — so a shared computer never paints
 * the previous person's nav. It is a hint about what to draw and nothing more: a stale cache offers
 * a row whose screen then says no, which is the same outcome as `unknown`.
 */
import { createContext, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { api, apiConfigured } from '../api/client'
import type { ProfileResource } from '../api/types'
import { useSession } from './session'

/** What `/v1/profile` said the caller may open. */
export type Offering =
  | { readonly kind: 'asking' }
  | {
      readonly kind: 'known'
      readonly permissions: readonly string[]
      /**
       * Whether "Ask to invite someone" is a door for this caller (ADR 0106) — the API's own
       * answer, carried beside the keys because the invite card at the foot of the nav is offered
       * to somebody who may invite OR may ask, and a member who may ask holds no key at all.
       */
      readonly inviteRequestsOpen?: boolean
    }
  | { readonly kind: 'unknown' }

/** The offering a component assumes with no provider above it: offer everything, let each screen's 403 decide. */
export const UNKNOWN_OFFERING: Offering = { kind: 'unknown' }

/**
 * Whether to OFFER something gated on ONE key: ungated always, and a gated one when the caller's
 * profile says they hold it. `unknown` offers everything and leaves each screen's own 403 to decide.
 */
export const offers = (offering: Offering, requires: string | undefined): boolean =>
  requires === undefined ||
  offering.kind === 'unknown' ||
  (offering.kind === 'known' && offering.permissions.includes(requires))

/**
 * Whether to offer something gated on ANY of several keys — an area whose screens carry different
 * keys, like the Admin console, is offered to somebody who may open at least one of them. An empty
 * list is "ungated".
 */
export const offersAny = (offering: Offering, requires: readonly string[] | undefined): boolean =>
  requires === undefined ||
  requires.length === 0 ||
  offering.kind === 'unknown' ||
  (offering.kind === 'known' && requires.some((key) => offering.permissions.includes(key)))

/**
 * Whether the invite door — the card at the foot of the nav, the "Invite people" item of the
 * International switcher — is worth offering: to somebody who may invite, or who may ask to.
 */
export const mayInvite = (offering: Offering): boolean =>
  offering.kind === 'unknown' ||
  (offering.kind === 'known' &&
    (offering.permissions.includes('members.invite') || offering.inviteRequestsOpen === true))

const CACHE_KEY = 'flowza.permissions'

type Cached = {
  readonly userId: string
  readonly permissions: readonly string[]
  readonly inviteRequestsOpen?: boolean
}

function cachedFor(userId: string | null): Offering {
  if (userId === null) return { kind: 'asking' }
  try {
    const raw = window.localStorage.getItem(CACHE_KEY)
    if (raw === null) return { kind: 'asking' }
    const parsed = JSON.parse(raw) as Partial<Cached> | null
    if (
      parsed !== null &&
      typeof parsed === 'object' &&
      parsed.userId === userId &&
      Array.isArray(parsed.permissions) &&
      parsed.permissions.every((k): k is string => typeof k === 'string')
    ) {
      return {
        kind: 'known',
        permissions: parsed.permissions,
        inviteRequestsOpen: parsed.inviteRequestsOpen === true,
      }
    }
  } catch {
    // A private window, cleared site data, a blocked accessor: no hint, and nothing to do about it.
  }
  return { kind: 'asking' }
}

function cache(userId: string | null, known: Omit<Cached, 'userId'>): void {
  if (userId === null) return
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify({ userId, ...known } satisfies Cached))
  } catch {
    // Same as above: the cache is a courtesy, never a requirement.
  }
}

/**
 * Read the caller's permissions from `/v1/profile`, once.
 *
 * `enabled` is the switch a component throws when a provider above it has already answered: hooks
 * cannot be called conditionally, so the read is, and a disabled read costs nothing and answers
 * `unknown`. `userId` keys the first-paint cache and is optional — a caller that does not know who
 * is signed in simply gets no hint.
 */
export function useProfileOffering(enabled: boolean, userId: string | null = null): Offering {
  const [offering, setOffering] = useState<Offering>(() => {
    if (!enabled) return { kind: 'unknown' }
    if (!apiConfigured()) return { kind: 'unknown' }
    return cachedFor(userId)
  })

  useEffect(() => {
    if (!enabled || !apiConfigured()) return
    let cancelled = false
    api<ProfileResource>('/v1/profile')
      .then((p) => {
        if (cancelled) return
        if (Array.isArray(p.permissions)) {
          const known = {
            permissions: p.permissions,
            inviteRequestsOpen: p.invite_requests_open === true,
          }
          setOffering({ kind: 'known', ...known })
          cache(userId, known)
        } else {
          // A Worker older than the field (S27). Offer everything; each screen's 403 decides.
          setOffering({ kind: 'unknown' })
        }
      })
      .catch(() => {
        if (!cancelled) setOffering({ kind: 'unknown' })
      })
    return () => {
      cancelled = true
    }
  }, [enabled, userId])

  return offering
}

/** `null` means no provider is mounted above the reader — see `useProfileOffering`. */
const PermissionsContext = createContext<Offering | null>(null)

/** The offering the nearest provider holds, or null where none is mounted. */
export function usePermissions(): Offering | null {
  return useContext(PermissionsContext)
}

/** The one read for the whole console. Mounted once, around the shell (`routes/index.tsx`). */
export function PermissionsProvider({
  children,
  userId,
}: {
  readonly children: ReactNode
  /** The signed-in account, for the first-paint cache. Read from the session when not given. */
  readonly userId?: string | null
}) {
  const session = useSession()
  const forCache = userId ?? (session.kind === 'signed_in' ? session.userId : null)
  const offering = useProfileOffering(true, forCache)
  return <PermissionsContext.Provider value={offering}>{children}</PermissionsContext.Provider>
}
